import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { JsonService } from '../../../services/core/json.service';
import { BidApiResources } from './bid.constants';

@Injectable({
  providedIn: 'root',
})
export class BidDataService {
  private baseURL;

  // Subject for proposal list refresh (when duplicate proposal is created)
  private proposalListRefresh = new Subject<boolean>();
  proposalListRefresh$ = this.proposalListRefresh.asObservable();

  // Method to trigger proposal list refresh
  triggerProposalRefresh(): void {
    this.proposalListRefresh.next(true);
  }

  constructor(private http: HttpClient, private jsonService: JsonService) {
    this.baseURL = BidApiResources;
  }

  list(model: any): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getBidTakeOff(payload: any): Observable<any> {
    // Extract bid_id for the path, rest as query params
    const { bid_id, ...params } = payload;
    const endPoint = `${this.baseURL.getTakeoff}/${bid_id}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  // getBidTakeOff(payload: any): Observable<any> {
  //   const endPoint = `${this.baseURL.getTakeoff}`;
  //   // Use POST, not GET
  //   return this.http.post<any>(endPoint, payload).pipe(
  //     tap((data) => this.jsonService.doStringify(data)),
  //     catchError(this.errorHandlerMessage)
  //   );
  // }

  getPagesGroupByTakeoff(id: any): Observable<any> {
    const endPoint = `${this.baseURL.getPageGroup}/${id}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getDrawings(id: any, payload: any): Observable<any> {
    let endPoint = `${this.baseURL.getDrawing}`;
    endPoint = endPoint.replace('{bid_id}', id);
    return this.http.get<any>(endPoint, { params: payload }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getDrawingPages(id: any, payload: any): Observable<any> {
    let endPoint = `${this.baseURL.getDrawingPages}`;
    endPoint = endPoint.replace('{document_id}', id);
    return this.http.get<any>(endPoint, { params: payload }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getIncompleteDrawings(id: any, payload: any): Observable<any> {
    payload.loadingService = 0;
    let endPoint = `${this.baseURL.getIncompleteDrawings}`;
    endPoint = endPoint.replace('{bid_id}', id);
    return this.http.get<any>(endPoint, { params: payload }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  deleteBidTakeOffDrawing(id: any, payload: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteDrawing}/${id}`;
    return this.http.delete<any>(endPoint, { body: payload }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  search(model: any): Observable<any> {
    const endPoint = `${this.baseURL.search}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  update(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateEstimator(id: any, estimatorId: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}/estimator`;
    return this.http.patch<any>(endPoint, { estimator_id: estimatorId }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /**
   * Go/No-Go decision — `bids.bid_decision`: `'bid'`, `'no_bid'`, or `null` when
   * the bid has not been reviewed yet. Recording a decision never hides the bid.
   * @see PATCH /api/v1/bids/{id}/bid-decision
   */
  updateBidDecision(id: any, decision: 'bid' | 'no_bid' | null): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}/bid-decision`;
    return this.http.patch<any>(endPoint, { bid_decision: decision }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateBidOffice(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateBidOffice}/${id}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  delete(id: any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  // Bulk delete: DELETE bids/delete/multiple with { bid_ids: [...] }
  deleteMultiple(bidIds: number[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { bid_ids: bidIds } }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  // Pre-check which of the selected bids can be deleted (and why not, for the rest)
  deleteAvailability(bidIds: number[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { bid_ids: bidIds }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  addBidders(bidID: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.addBidder}/${bidID}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateBidders(bidID: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.addBidder}/${bidID}`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  OnHold(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.onHold}/${id}`;
    model.loadingService = 0;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  removeBidder(
    bidID: number,
    customerID: number,
    contactID: number
  ): Observable<any> {
    const endPoint = `${this.baseURL.removeBidder}/${bidID}/${customerID}/${contactID}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  onChangeStatus(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  archiveBid(id: any): Observable<any> {
    const endPoint = `${this.baseURL.archiveBid}/${id}/archive`;
    return this.http.post<any>(endPoint, {}).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  copyRebid(id: any): Observable<any> {
    const endPoint = `${this.baseURL.copyRebid}/${id}/rebid/copy`;
    return this.http.post<any>(endPoint, {}).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  activateRebid(id: any): Observable<any> {
    const endPoint = `${this.baseURL.activateRebid}/${id}/rebid/activate`;
    return this.http.post<any>(endPoint, {}).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getRebidHistory(id: any): Observable<any> {
    const endPoint = `${this.baseURL.rebidHistory}/${id}/rebid-history`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  // Related Jobs — every job ever created from this bid (via Cancel Job &
  // Award Another Bidder), active or archived, oldest first.
  getRelatedJobs(id: any): Observable<any> {
    const endPoint = `${this.baseURL.relatedJobs}/${id}/related-jobs`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  // Proposals belonging to this bid — same endpoint the Bid detail page's own
  // PROPOSALS tab uses (bid-proposal.component.ts), reused here for the Cancel
  // Job & Rebid picker so the proposal list and its fields stay identical.
  getProposalsByBid(bidId: any, search?: string): Observable<any> {
    let endPoint = `${this.baseURL.getProposalsByBid}/${bidId}`;
    if (search?.trim()) {
      endPoint += `?search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateUnionOptions(id: any, model: any): Observable<any> {
    model.loadingService = false;
    const endPoint = `${this.baseURL.updateUnionOptions}/${id}`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  copyProposal(id: any, model: any): Observable<any> {
    model.loadingService = false;
    const endPoint = `${this.baseURL.copyProposal}/${id}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getMediaFiles(model: any): Observable<any> {
    const endPoint = `${this.baseURL.getMediaFiles}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createMediaFiles(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createMediaFiles}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createFolderDrawing(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createFolderDrawing}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createRequestTakeoff(model: any): Observable<any> {
    const endPoint = `${this.baseURL.requestTakeoff}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  // Import methods following customer import pattern
  downloadSampleFile(): Observable<Blob> {
    const endPoint = `${this.baseURL.downloadSampleFile}`;
    return this.http
      .get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(formData: FormData): Observable<any> {
    const endPoint = `${this.baseURL.uploadImportFile}`;
    return this.http.post<any>(endPoint, formData).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  validateImportData(payload: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImportData}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  executeImport(payload: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getImportHistory(params: any): Observable<any> {
    // Use generic import history endpoint - this would need to be added to constants
    // For now, create a generic endpoint URL
    const endPoint = `${this.baseURL.list.replace('/bids', '/import-history')}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateSiteInfo(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateSiteInfo}/${id}`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getJobsiteRequirements(params?: any): Observable<any> {
    const endPoint = `${this.baseURL.getJobsiteRequirements}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getJobsiteAccesses(params: any): Observable<any> {
    const endPoint = `${this.baseURL.getJobsiteAccesses}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createJobsiteAccess(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createJobsiteAccess}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getJobsiteParking(params: any): Observable<any> {
    const endPoint = `${this.baseURL.getJobsiteParking}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createJobsiteParking(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createJobsiteParking}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createBidJobsite(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createBidJobsite}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getBidJobsites(bidId: number): Observable<any> {
    let endPoint = `${this.baseURL.getBidJobsites}`;
    endPoint = endPoint.replace('{bid_id}', bidId.toString());
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateDescription(id: any, description: string): Observable<any> {
    const endPoint = `${this.baseURL.updateDescription}/${id}/description`;
    return this.http.patch<any>(endPoint, { description }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateBidDueDate(
    bidId: number,
    params: { reason_for_change: string; due_date: string; due_time: string }
  ): Observable<any> {
    let endPoint = `${this.baseURL.updateBidDueDate}`;
    endPoint = endPoint.replace('{bid_id}', bidId.toString());

    // Create query parameters
    const queryParams = new HttpParams()
      .set('reason_for_change', params.reason_for_change || '')
      .set('due_date', params.due_date)
      .set('due_time', params.due_time);

    return this.http.patch<any>(endPoint, {}, { params: queryParams }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /** Immediate, single-page manual scale correction — no takeoff involved. */
  updateDrawingPageScale(pageId: any, scaleId: number): Observable<any> {
    const endPoint = `${this.baseURL.updateDrawingPageScale}`.replace('{id}', pageId);
    return this.http.patch<any>(endPoint, { scale_id: scaleId }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /** Undo manual overrides for the given pages, restoring Gemini's original read. */
  resetDrawingPageScale(pageIds: number[]): Observable<any> {
    const endPoint = `${this.baseURL.resetDrawingPageScale}`;
    return this.http.post<any>(endPoint, { bid_drawing_page_ids: pageIds }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /** Deletes one drawing page (its split-PDF file plus the row) — refused by the
   * backend if that page is already attached to a takeoff. */
  deleteDrawingPage(pageId: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteDrawingPage}`.replace('{id}', pageId);
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
